import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '@environment/environment';
import {catchError, retry} from "rxjs/operators";
import {Observable, throwError, finalize} from 'rxjs';
import {HttpHeaders} from '@angular/common/http';
import {LoaderService} from '@services/loader/loader.service';
import {MessagesService} from '@services/messages/messages.service';
import {ApiResponse} from '@interfaces/response';
import {RequestOptions} from '@interfaces/request_options';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private loadingService: LoaderService = inject(LoaderService);
  private messageService: MessagesService = inject(MessagesService);

  /**
   * Contiene URL di base per le chiamate REST
   * @var {string}
   */
  private url: string = environment.api.base_url;

  /**
   * Costruttore del service
   *
   * @param HttpClient http effettua le chiamate REST
   * @param Config config gestisce i dati di configurazione
   */
  private http = inject(HttpClient);

  /**
   * Effettua la chiamata GET
   * @param string endpoint url (parte finale) dove effettuare
   * @param array params parametri (key:value) da passare alla chiamata GET
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  get<T>(endpoint: string, params?: any, reqOpts?: RequestOptions, loaderShow = true): Observable<any> {
    if (loaderShow) {
      this.loadingService.show();
    }

    if (!reqOpts) {
      reqOpts = {};
    }

    if (params) {
      let httpParams = new HttpParams();
      for (const k in params) {
        if (params.hasOwnProperty(k)) {
          httpParams = httpParams.set(k, params[k]);
        }
      }
      reqOpts.params = httpParams;
    }
    return this.http.get<ApiResponse<T>>(this.url + endpoint, reqOpts).pipe(
      retry(1),
      catchError(this.handleError),
      finalize(() => this.loadingService.hide())
    );
  }

  getExternalUrl(endpoint: string, params?: any, reqOpts?: RequestOptions, loaderShow = true) {
    if (loaderShow) {
      this.loadingService.show();
    }
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams()
      };
    }
    if (params) {
      reqOpts.params = new HttpParams();
      for (const k of params) {
        reqOpts.params = reqOpts.params.set(k, params[k]);
      }
    }
    return this.http.get(endpoint, reqOpts).pipe(retry(1), catchError(this.handleError))
  }

  /**
   * Effettua la chiamata POST
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  post<T>(endpoint: string, body: any, params?: any, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams()
      };
    }
    if (params) {
      reqOpts.params = new HttpParams();
      for (const k in params) {
        reqOpts.params = reqOpts.params.set(k, params[k]);
      }
    }
    return this.http.post<ApiResponse<T>>(this.url + endpoint, body, {
      ...reqOpts,
      observe: 'body',
      responseType: 'json'
    }).pipe(
      retry(1),
      catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Effettua la chiamata PUT
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON} body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  put<T>(endpoint: string, body: any, params?: any, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams()
      };
    }
    if (params) {
      reqOpts.params = new HttpParams();
      for (const k of params) {
        reqOpts.params = reqOpts.params.set(k, params[k]);
      }
    }
    return this.http.put<ApiResponse<T>>(this.url + endpoint, body, reqOpts).pipe(retry(1), catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide()))
  }

  /**
   * Effettua la chiamata DELETE
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  delete<T>(endpoint: string, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    return this.http.delete<ApiResponse<T>>(this.url + endpoint, reqOpts).pipe(retry(1), catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide()))
  }

  /**
   * Effettua la chiamata PATCH
   * @param string endpoint url (parte finale) dove effettuare
   * @param JSON body contiene i valori da passare alla chiamata in formato JSON
   * @param HttpParams reqOpts  parametri da utilizzare nell'intestazione
   * @return Observable
   */
  patch<T>(endpoint: string, body: any, reqOpts?: RequestOptions): Observable<any> {
    this.loadingService.show();
    return this.http.patch<ApiResponse<T>>(this.url + endpoint, body, reqOpts).pipe(retry(1), catchError(this.handleError.bind(this)),
      finalize(() => this.loadingService.hide()))
  }


  private handleError = (error: any) => {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    if (error?.error?.message) {
      errorMessage = error.error.message;
    }
    if (errorMessage && this.messageService) {
      this.messageService.error('error', errorMessage, 5000);
    }
    return throwError(() => errorMessage);
  }

}
